/* @flow weak */
import { Observable } from 'rxjs';
import { LOCATION_CHANGE } from 'react-router-redux';
import { browserHistory } from 'react-router';
import { combineEpics } from 'redux-observable';
import { Map, fromJS, List } from 'immutable';
import { prop } from 'ramda';
import { capitalize } from 'trustar/utils';
import createDebugger from 'debug';
const debug = createDebugger('app:modules:search:epics'); // eslint-disable-line no-unused-vars

import {
  TRIGGER_SEARCH,
  EXECUTE_SEARCH,
  EXECUTE_REPORTS_SEARCH,
  SEARCH_REPORTS_FAILURE,
  SEARCH_REPORTS_SUCCESS, UPDATE_REPORT_PAGE_FILTERS,
  EXECUTE_ENTITIES_SEARCH,
  SEARCH_ENTITIES_SUCCESS,
  SEARCH_ENTITIES_FAILURE,
  UPDATE_ENTITY_PAGE_FILTERS,
  CLEAR_REPORT_SEARCH_FILTERS,
  CLEAR_ENTITY_SEARCH_FILTERS,
  CLEAR_SEARCH_PAGE_FILTERS,
  UPDATE_SEARCH_PAGE_FILTERS,

} from './action-types';
import { padWith } from 'modules/reports/utils';
import {
  searchV3Endpoint,
  searchV3ReportEndpoint,
  searchV3EntityEndpoint,
} from './constants';

import * as Actions from './actions';
import * as Getters from './getters';
import * as Enclave from 'modules/enclave';
import * as Profile from 'modules/profile';
import { loading as appIsLoading, isLoggedIn } from 'modules/app/getters';
import {lastRoute, routeHistory} from 'modules/app/getters';
import { LOGIN_SUCCESS } from '../app/action-types';
import {EXECUTE_SEARCH_AFTEWARDS} from '../../components/browse/Search2.0/search.constants';

const handleSearchErrorResponse = (response: Object) => {
  const DEFAULT_ERROR_MESSAGE = 'Could not perform search, please contact support if the problem persists.';

  if (!response) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return response.exception || response.message || DEFAULT_ERROR_MESSAGE;
};

/* Initiate search action: routes search query to url */
const routeSearchEpic = (action$) =>
  action$.ofType(TRIGGER_SEARCH)
    .map(prop('payload'))
    .do(search => {
      browserHistory.push({
        pathname: '/browse/search',
        search: `?q=${encodeURIComponent(search)}`,
      });
    })
    .ignoreElements();

/* Main epic to handle running a search */
const searchEpic = (action$, store) =>
  action$.ofType(EXECUTE_SEARCH)
    .mergeMap(action => Observable.defer(() => {
      const { payload: searchTerm } = action;
      const state = store.getState();
      const userLoggedIn = isLoggedIn(state);
      if (appIsLoading(state) || !userLoggedIn) {
        return Observable.of(action).delay(1000);
      }

      const searchPayload = {
        fields: ['query', 'pageNumber'],
        payload: Map({
          pageNumber: 0,
          query: searchTerm,
        }),
        dispatchSearchAfterUpdate: true, // We update the query term and then execute search -- no race!
      };

      return [
        Actions.setViewPanel('ENTITIES'),
        { type: UPDATE_REPORT_PAGE_FILTERS, ...searchPayload},
        { type: UPDATE_ENTITY_PAGE_FILTERS, ...searchPayload},
      ];

    })
    .catch(err => {
      // TODO_LOG // TODO_LOG captureError(err);
      return Observable.empty();
    }));

// When you clear the form on the top search bar (i.e. click on the X), this will trigger a search with the new query payload
const clearSearchFormEpic = (action$, store) =>
  action$.ofType('@@redux-form/RESET')
    .filter(action => action.meta && action.meta.form === 'search')
    .map(() => {
      return Actions.executeSearch('');
    });

// Search 2.0
export const formatSearchRequest = (payload: Map<string, any>) => {
  const formattedFilters = payload.get('filters')
    .reduce((finalFilterArray, filterMap, filterName) => {
      return finalFilterArray.concat(
        {
          field: filterName,
          ...filterMap.toObject(),
        }
        );
    }, []);

  const justTagNames = payload.get('tagFilters')
    .update('included', tagObjects => tagObjects.map(t => t.get('name')))
    .update('excluded', tagObjects => tagObjects.map(t => t.get('name')));

  return payload.set('filters', formattedFilters)
    .set('tagFilters', justTagNames)
    .update('sort', sort => sort.has('sort') ? sort.get('sort') : sort);
};

const formatSearchResponse = (payload: Object, searchType: string) => {
  if (!payload) return Map();

  const formattedPayload = Map(payload)
    .set('searchType', searchType)
    .update('items', (items = List()) => {
      return items.map(item => Map(item));
    });

  return formattedPayload;
};

const searchReportsRequest = (action$, store, { ajax }) =>
  action$.ofType(EXECUTE_REPORTS_SEARCH)
    .map(prop('payload'))
    .map(formatSearchRequest)
    .map(payload => payload.set('type', 'INCIDENT_REPORT'))
    .switchMap((searchDto) => {
      return ajax.postJSON(searchV3ReportEndpoint, searchDto)
        .mergeMap(({ response }) => {
          return Observable.of({
            type: SEARCH_REPORTS_SUCCESS,
            payload: formatSearchResponse(response, 'reports'),
          });
        })
        .catch(err => {
          const humanErrorMessage = handleSearchErrorResponse(err.response);
          // TODO_LOG // TODO_LOG captureError(err);
          return Observable.of({
            type: SEARCH_REPORTS_FAILURE,
            error: true,
            payload: err,
            meta: {
              alert: { type: 'error', text: humanErrorMessage},
            },
          });
        });
    });

const searchEntitiesRequest = (action$, store, { ajax }) =>
  action$.ofType(EXECUTE_ENTITIES_SEARCH)
    .map(prop('payload'))
    .map(formatSearchRequest)
    .map(payload => payload.set('type', 'INDICATOR'))
    .switchMap((searchDto) => {
      return ajax.postJSON(searchV3EntityEndpoint, searchDto)
        .mergeMap(({ response }) => {
          return Observable.of({
            type: SEARCH_ENTITIES_SUCCESS,
            payload: formatSearchResponse(response, 'entities'),
          });
        })
        .catch(err => {
          const humanErrorMessage = handleSearchErrorResponse(err.response);
          // TODO_LOG captureError(err);
          return Observable.of({
            type: SEARCH_ENTITIES_FAILURE,
            error: true,
            payload: err,
            meta: {
              alert: { type: 'error', text: humanErrorMessage},
            },
          });
        });
    });

  /*
    This epic may be a bit overloaded, but it handles updates and clears for reports and iocs
   We are looking for filter payload updates with a flag to execute a search immediately after the update,
   think any auto-updating filter clicks (likes sorter)
  */
const updateSearchPayloadEpic = (action$, store) =>
  action$.ofType(UPDATE_REPORT_PAGE_FILTERS, UPDATE_ENTITY_PAGE_FILTERS)
    .filter(action => !!action.dispatchSearchAfterUpdate)
    .mergeMap(action => {
      const state = store.getState();
      const searchToExecute = action.type === UPDATE_REPORT_PAGE_FILTERS ? EXECUTE_REPORTS_SEARCH : EXECUTE_ENTITIES_SEARCH;
      const payload = action.type === UPDATE_REPORT_PAGE_FILTERS ? Getters.reportPageQuery(state) : Getters.entityPageQuery(state);
      return Observable.defer(() => {
        return [
          { type: searchToExecute, payload },
        ];
      });
    }
    )
    .catch(err => {
      // TODO_LOG captureError(err);
      return Observable.empty();
    });

/*
  Set the filters to their defaults, if dispatchSearchAfterUpdate is set to true then we will
  search with default params -- giving us what /browse used to be, a get-all search.
  Filters go back to their defaults regardless.
 */
const clearSearchPayloadEpic = (action$, store) =>
    action$.ofType(CLEAR_REPORT_SEARCH_FILTERS, CLEAR_ENTITY_SEARCH_FILTERS)
      .mergeMap(action => {
        const state = store.getState();
        /* NOTE: Must have Profile and Enclaves loaded first */
        const profileLoading = Profile.getters.loading(state);
        const enclaveLoading = Enclave.getters.loading(state);
        const userLoggedIn = isLoggedIn(state);

        if (!userLoggedIn) return Observable.empty();

        if (profileLoading || enclaveLoading) {
          return Observable.of(action).delay(500);
        }

        // Grab our default enclaves for the user and set them
        const defaultEnclaves = Profile.getters.privateEnclavesIdSet(state);
        const payload = Map({'enclaveFilters': defaultEnclaves});

        if (action.onGoingSearch) {
          payload.set('query', action.onGoingSearch);
        }

        const updateType = action.type === CLEAR_REPORT_SEARCH_FILTERS ?
        UPDATE_REPORT_PAGE_FILTERS : UPDATE_ENTITY_PAGE_FILTERS;
        return Observable.defer(() => {
          return [
            { type: updateType,
              payload,
              fields: ['enclaveFilters'],
              dispatchSearchAfterUpdate: action.dispatchSearchAfterUpdate
            },
            { type: UPDATE_SEARCH_PAGE_FILTERS,
              payload,
              fields: ['enclaveFilters'],
              dispatchSearchAfterUpdate: action.dispatchSearchAfterUpdate
            },
          ];
        });
      }
      )
      .catch(err => {
        // TODO_LOG captureError(err);
        return Observable.empty();
      });


// Search Page epics largely translate to actions for reports and entities, we just need to do some filtering and data modification for them
const splitSearchQueryUpdatesEpic = (action$, store) =>
    action$.ofType(UPDATE_SEARCH_PAGE_FILTERS, CLEAR_SEARCH_PAGE_FILTERS)
      .mergeMap(action => {
        let actions;

        if (action.type === CLEAR_SEARCH_PAGE_FILTERS) {
          actions = [
            {...action, type: CLEAR_REPORT_SEARCH_FILTERS },
            {...action, type: CLEAR_ENTITY_SEARCH_FILTERS },
            Actions.resetSearch(),
          ];
        } else {
          actions = [
            {...action, type: UPDATE_REPORT_PAGE_FILTERS },
            {...action, type: UPDATE_ENTITY_PAGE_FILTERS },
          ];
        }

        return Observable.defer(() => {
          return actions;
        });
      })
      .catch(err => {
        // TODO_LOG captureError(err);
        return Observable.empty();
      });

const initializeSearchResultsEpic = (action$, store) =>
  action$.ofType(LOGIN_SUCCESS)
  .mergeMap(action => {
    const state = store.getState();

    /* NOTE: Must have Profile and Enclaves loaded first */
    const profileLoading = Profile.getters.loading(state);
    const enclaveLoading = Enclave.getters.loading(state);
    const onGoingSearch = Getters.searchTerm(state);

    if (profileLoading || enclaveLoading) {
      return Observable.of(action).delay(500);
    }

    return Observable.defer(() => {
      return onGoingSearch ?
        [
          { type: EXECUTE_SEARCH, payload: onGoingSearch },
        ]
        :
       [
        { type: CLEAR_SEARCH_PAGE_FILTERS, dispatchSearchAfterUpdate: true },
      ];
    });
  }
  )
  .catch(err => {
    // TODO_LOG captureError(err);
    return Observable.empty();
  });

export default combineEpics(
  routeSearchEpic,
  searchEpic,
  clearSearchFormEpic,
  searchReportsRequest,
  updateSearchPayloadEpic,
  searchEntitiesRequest,
  clearSearchPayloadEpic,
  splitSearchQueryUpdatesEpic,
  initializeSearchResultsEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/search/epics.js